import { useCallback, useEffect, useState } from "react";
import { queryClient } from "$utils/queryClient";
import { EDBService, OrderService } from "$services";
import { Button } from "$components";
import { CacheInvalidationAction } from "$utils/cache";

const buttonContainerStyle = {
  display: "flex",
  gap: "4px",
  maxWidth: "100%",
  overflow: "hidden",
};

const buttonStyle = {
  marginLeft: 8,
  marginTop: 4,
  marginBottom: 4,
};

export const ButtonsList = ({
  ops,
  editReceptionClick,
  order,
  receptions,
  isReadOnly,
  reference,
  currentCompany,
  suborder,
}: any) => {
  const [edbs, setEdbs] = useState([]);
  const [currentOps, setCurrentOps] = useState([]);

  const fetchEdbsByOrderReference = useCallback(async () => {
    try {
      if (order?.id) {
        // Check cache first to avoid duplicate API calls
        const cacheKey = [CacheInvalidationAction.GET_ORDER_IN_EDB, order.id];

        let data: any = queryClient.getQueryData(cacheKey);

        if (!data) {
          // Fetch and cache the result
          data = await queryClient.fetchQuery({
            queryKey: cacheKey,
            queryFn: () => OrderService.getIsOrderInAnEDB(order.id),

            // Cache for 5 minutes
            gcTime: 5 * 60 * 1000,

            staleTime: 5 * 60 * 1000,
          });
        }

        if (data?.error) {
          console.error("Error fetching EDBs by order ID:", data.error);
          setEdbs([]);
        } else {
          setEdbs(data || []);
        }
      } else {
        setEdbs([]);
      }
    } catch (error) {
      console.error("Error fetching EDBs by order ID:", error);
      setEdbs([]);
    }
  }, [order?.id]);

  useEffect(() => {
    fetchEdbsByOrderReference();
  }, [fetchEdbsByOrderReference, order?.id]);

  const [currentEdbs, setCurrentEdbs] = useState<any>();

  // receptions suborder
  const getCurrentReception = useCallback(
    (orderRef: string, suborderRef: string) => {
      const currentReceptions = receptions?.filter((r: any) =>
        r.orders?.find(
          (o: any) =>
            o.reference === orderRef &&
            o.suborders?.find((s: any) => s.reference === suborderRef)
        )
      );

      return currentReceptions;
    },
    [receptions]
  );

  const getCurrentEdbs = useCallback(
    async (orderRef: string, suborderRef: string) => {
      if (edbs && suborder?.id) {
        // Filter EDBs that have orderProducts matching the current suborder ID
        const filteredEdbs = edbs.filter((edb: any) => {
          return edb.orderProducts?.some((orderProduct: any) => {
            return orderProduct.suborderId === suborder.id;
          });
        });

        // Extract EDB IDs to fetch complete EDB data
        const edbIds = filteredEdbs.map((edb: any) => edb.edbId);

        if (edbIds.length > 0) {
          try {
            // Check cache first
            let currentEdbs = await queryClient.getQueryData([
              CacheInvalidationAction.GET_EDBS,
              currentCompany,
              { $inId: edbIds },
            ]);

            if (!currentEdbs) {
              // Fetch complete EDB data if not in cache
              currentEdbs = await queryClient.fetchQuery({
                queryKey: [
                  CacheInvalidationAction.GET_EDBS,
                  currentCompany,
                  { $inId: edbIds },
                ],

                queryFn: () => EDBService.getEDBsByIds(edbIds),
                gcTime: 5 * 60 * 1000,
              });
            }

            setCurrentEdbs((currentEdbs as any)?.data || currentEdbs);
          } catch (error) {
            console.error("Error fetching complete EDB data:", error);
            setCurrentEdbs([]);
          }
        } else {
          setCurrentEdbs([]);
        }

        // Find the current suborder by reference from reception data for OPs filtering
        let currentSuborderProductRefs = [];

        if (receptions && receptions.length > 0) {
          // Look through all receptions to find the current suborder
          const receptionWithCurrentSuborder = receptions.find((reception) => {
            // Check if this reception contains the current suborder
            return reception.receptionReferences?.some((ref) =>
              ref.suborderReferences?.includes(suborderRef)
            );
          });

          if (receptionWithCurrentSuborder) {
            // Get product references from receptionReferences that match the current suborder
            const matchingReceptionRefs =
              receptionWithCurrentSuborder.receptionReferences?.filter((ref) =>
                ref.suborderReferences?.includes(suborderRef)
              ) || [];

            // Extract productReference from matching reception references
            currentSuborderProductRefs = matchingReceptionRefs
              .map((ref) => ref.productReference)
              .filter(Boolean); // Remove any undefined/null values
          }
        }

        // Filter OPs to only include those with matching product references
        const filteredOps = ops?.filter((op) => {
          const hasMatchingProduct = op.products.some((product) => {
            const productRef = product.relatedProduct?.reference;
            const isMatch = currentSuborderProductRefs.includes(productRef);
            return isMatch;
          });

          return hasMatchingProduct;
        });

        setCurrentOps(filteredOps);
      }
    },
    [edbs, suborder, receptions, ops, currentCompany]
  );

  useEffect(() => {
    getCurrentEdbs(order?.reference, reference);
  }, [getCurrentEdbs, order?.reference, reference, currentCompany]);

  const currentReceptions =
    receptions && order
      ? getCurrentReception(order.reference, reference)
      : undefined;

  return (
    <div style={buttonContainerStyle}>
      {currentReceptions &&
        currentReceptions.map((currentReception: any) => {
          return (
            <Button
              key={currentReception.reference}
              style={buttonStyle}
              variant="outlined"
              onClick={() => editReceptionClick(currentReception)}
            >
              {currentReception.reference}
            </Button>
          );
        })}
      {currentOps &&
        currentOps.map((currentOP: any) => {
          return (
            <Button
              key={currentOP.code}
              disabled={isReadOnly}
              style={buttonStyle}
              variant="outlined"
              onClick={() =>
                window.open(
                  `${window.location.protocol}//${window.location.host}/edbs/${currentOP.edb._id}?tabpane=3`,
                  "_blank"
                )
              }
            >
              {currentOP.code}
            </Button>
          );
        })}
      {currentEdbs &&
        currentEdbs.map((currentEdb: any) => {
          return (
            <Button
              key={currentEdb.edbId}
              style={buttonStyle}
              variant="outlined"
              onClick={() =>
                window.open(
                  `${window.location.protocol}//${window.location.host}/edbs/${currentEdb._id}?tabpane=1`,
                  "_blank"
                )
              }
            >
              {currentEdb.edbId}
            </Button>
          );
        })}
    </div>
  );
};
